import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Table, TableColumnConfig, EditIcon, DeleteIcon, Truncated, Text } from '@frontend/design-system';
import { useAvailabilityData } from '../../contexts/availability-data-context';
import { AvailabilityRule, AvailabilityScheduleType } from '../../types/availability-rules.types';
import { abbreviateDays, formatTime } from '../../utils/utilities';
import { BOOKING_CADENCE_OPTIONS, RECURRENCE_OPTIONS } from './constants';
import { EmptyState } from './empty-state';

export const AvailabilityTable = ({
  onEdit,
  onDelete,
  availabilityRules,
}: {
  onEdit: (template: AvailabilityRule) => void;
  onDelete: (template: AvailabilityRule) => void;
  availabilityRules?: AvailabilityRule[];
}) => {
  const { t } = useTranslation('schedule-settings');
  const { isLoading } = useAvailabilityData();

  const colConfig: TableColumnConfig<AvailabilityRule>[] = [
    {
      id: 'recurrence',
      Header: t('Recurrence'),
      accessor: 'scheduleType',
      disableSortBy: true,
      minWidth: 120,
      cellRenderer: (cellValue: string) => {
        const label = RECURRENCE_OPTIONS.find((o) => o.value === cellValue)?.label ?? cellValue;
        return <Text>{label}</Text>;
      },
    },
    {
      id: 'schedule',
      Header: t('Schedule'),
      accessor: 'scheduleType',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (_, rowData) => {
        if (rowData?.scheduleType === 'SPECIFIC_DATE') {
          const date = rowData.date ? formatDate(rowData.date, 'MMM D, YYYY') : '';

          return <Text>{date}</Text>;
        }
        if (rowData?.scheduleType === 'RECURRENCE') {
          const startDate = rowData.date ? formatDate(rowData.date, 'MMM D, YYYY') : '';
          const unitMap: Record<string, string> = { WEEKLY: 'week', MONTHLY: 'month', DAILY: 'day', YEARLY: 'year' };
          const unitKey = unitMap[rowData.recurrenceUnit?.toUpperCase()] ?? rowData.recurrenceUnit?.toLowerCase() ?? '';
          const unit = t(unitKey, { count: rowData.recurrenceCount });
          const every = t('Every {{interval}} {{unit}} starting {{startDate}}', {
            count: rowData.recurrenceCount,
            interval: rowData.recurrenceCount,
            unit,
            startDate,
          });
          return <Text>{every}</Text>;
        }
        if (rowData?.scheduleType === 'WEEKLY') {
          const text = abbreviateDays(rowData.days ?? []).join(', ');
          return <Text>{text}</Text>;
        }
        return <Text>-</Text>;
      },
    },
    {
      id: 'hours',
      Header: t('Hours'),
      disableSortBy: true,
      minWidth: 150,
      cellRenderer: (_, rowData) => {
        const startTime = formatTime(rowData?.timeSlot.start ?? '');
        const endTime = formatTime(rowData?.timeSlot.end ?? '');
        return `${startTime} - ${endTime}`;
      },
    },
    {
      id: 'services',
      Header: t('Services'),
      accessor: 'services',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (cellValue: AvailabilityRule['services']) => {
        const text = cellValue.map((s) => s.name).join(', ');
        return <Truncated>{t(`{{text}}`, { text })}</Truncated>;
      },
    },
    {
      id: 'providers',
      Header: t('Provider'),
      accessor: 'providers',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (cellValue: AvailabilityRule['providers']) => {
        const text = cellValue.map((p) => p.name).join(', ');
        return <Truncated>{text}</Truncated>;
      },
    },
    {
      id: 'startDate',
      Header: t('Start Date'),
      accessor: 'date',
      disableSortBy: true,
      maxWidth: 150,
      cellRenderer: (cellValue: string, rowData) => {
        if (rowData?.scheduleType === AvailabilityScheduleType.SPECIFIC_DATE) {
          return null;
        }
        const startDate = cellValue ? formatDate(cellValue, 'MMM D, YYYY') : '';
        return <Text>{startDate}</Text>;
      },
    },
    {
      id: 'bookingInterval',
      Header: t('Booking Cadence'),
      accessor: 'bookingInterval',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => {
        const label = BOOKING_CADENCE_OPTIONS.find((o) => o.value === cellValue)?.label ?? t('None');
        return <Text>{label}</Text>;
      },
    },
  ];

  return (
    <Table
      colConfig={colConfig}
      data={availabilityRules || []}
      isLoading={isLoading}
      tableStyle={tableStyle}
      disableMultiSort
      emptyStateConfig={<EmptyState description={t('No availability templates found')} />}
      rowActions={{
        actions: [
          {
            label: t('Edit'),
            Icon: EditIcon,
            onClick: (rowData) => onEdit(rowData),
          },
          {
            label: t('Delete'),
            Icon: DeleteIcon,
            onClick: (rowData) => onDelete(rowData),
          },
        ],
      }}
    />
  );
};

// Don't judge my !important usage here... Table component adds borders that I can't override
const tableStyle = css({
  borderRadius: '0 !important',
  borderTop: 'none !important',
  borderLeft: 'none !important',
  borderRight: 'none !important',
});
